import type { ProgressUpdate } from '../types';

export class ProgressWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private onUpdateCallback: ((update: ProgressUpdate) => void) | null = null;
  private onErrorCallback: ((error: Event) => void) | null = null;
  private onCompleteCallback: (() => void) | null = null;

  constructor(private sessionId: string) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/v1/progress/${this.sessionId}`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected to progress updates');
        this.reconnectAttempts = 0;
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle connection confirmation
          if (data.type === 'connected') {
            console.log('[WebSocket] Progress updates ready');
            return;
          }

          // Handle completion message
          if (data.type === 'closed') {
            console.log('[WebSocket] Progress updates completed');
            this.onCompleteCallback?.();
            this.close();
            return;
          }

          // Handle progress update
          const update = data as ProgressUpdate;
          this.onUpdateCallback?.(update);

          // Auto-close on complete
          if (update.stage === 'complete') {
            setTimeout(() => {
              this.onCompleteCallback?.();
              this.close();
            }, 1000);
          }
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        this.onErrorCallback?.(error);
        reject(error);
      };

      this.ws.onclose = (event) => {
        console.log('[WebSocket] Closed:', event.code, event.reason);

        // Attempt reconnection if not a clean close
        if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`[WebSocket] Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

          setTimeout(() => {
            this.connect().catch(console.error);
          }, this.reconnectDelay * this.reconnectAttempts);
        }
      };
    });
  }

  onUpdate(callback: (update: ProgressUpdate) => void): void {
    this.onUpdateCallback = callback;
  }

  onError(callback: (error: Event) => void): void {
    this.onErrorCallback = callback;
  }

  onComplete(callback: () => void): void {
    this.onCompleteCallback = callback;
  }

  close(): void {
    if (this.ws) {
      this.ws.close(1000, 'Client closing connection');
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
